import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import LastUpdated from "../components/LastUpdated";
import { BlogRequestState } from "../components/BlogRequestState";
import LegacyBlogPage from "./LegacyBlogPage";
import { formatDate, PAGE_UPDATED, webPageLd, breadcrumbLd, SITE_URL } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [searchParams] = useSearchParams();
  if (searchParams.has("post")) return <LegacyBlogPage slug={searchParams.get("post")} />;
  return <BlogIndex />;
}

function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    axios.get(`${API}/blog?limit=200`, { signal: controller.signal, timeout: 30000 })
      .then((r) => setPosts(r.data || []))
      .catch((e) => { if (!axios.isCancel(e)) setError(e.response?.status || 503); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [attempt]);

  if (error) return <BlogRequestState error={error} retry={() => setAttempt((value) => value + 1)} />;

  return (
    <>
      <Seo
        title="Austin Concrete & Xeriscape Guides | Texan Strong Builds"
        description="Expert articles on concrete patios, stamped concrete, driveways, concrete repair and xeriscape landscaping in Austin TX from Texan Strong Builds."
        canonical={`${SITE_URL}/blog`}
        jsonLd={[
          webPageLd({
            name: "Blog | Austin Concrete, Stamped Concrete, Xeriscape & Driveway Guides",
            path: "/blog",
            updated: PAGE_UPDATED.blog,
            description: "Expert articles on concrete and xeriscape landscaping in Austin TX from Texan Strong Builds.",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
          ...(posts.length
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: "Texan Strong Builds Blog Articles",
                  itemListElement: posts.map((p, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: p.title,
                    url: `${SITE_URL}/blog/${p.slug}`,
                  })),
                },
              ]
            : []),
        ]}
      />

      <section className="bg-stone-900 text-white pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs dark items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]} />
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500">The TSB Blog</div>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            Real answers for<br />Austin property owners.
          </h1>
          <p className="mt-7 text-stone-300 text-lg max-w-3xl">
            Pricing, permits, plant lists, finishes — every article is written by the Texan Strong
            Builds team that pours and plants across Austin, TX every day. No fluff, no
            marketing-speak.
          </p>
          <LastUpdated dark date={PAGE_UPDATED.blog} className="mt-5" />
        </div>
      </section>

      <section className="bg-stone-50 py-20 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/5] bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map((p) => (
                <article
                  key={p.id}
                  data-testid={`blog-page-card-${p.slug}`}
                  className="group bg-white border border-stone-200 hover:shadow-xl transition-shadow"
                >
                  <Link to={`/blog/${p.slug}`} className="block" data-testid={`blog-article-link-${p.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden border-b border-stone-200">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                      />
                    </div>
                    <div className="p-7">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-bold">
                        <span className="text-red-600">{p.category}</span>
                        <span className="text-stone-500 flex items-center gap-1.5">
                          <Clock size={12} /> {p.read_time}
                        </span>
                      </div>
                      <h2 className="mt-3 font-display text-xl font-bold text-stone-900 leading-snug tracking-tight group-hover:text-red-600 transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 flex items-center justify-between gap-3">
                        <span className="inline-flex items-center gap-2 text-xs font-bold text-stone-900">
                          Read article <ArrowRight size={14} />
                        </span>
                        <time
                          dateTime={p.updated_at || p.published_at}
                          className="text-[11px] text-stone-500"
                          data-testid={`blog-card-updated-${p.slug}`}
                        >
                          Updated {formatDate(p.updated_at || p.published_at)}
                        </time>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
